<template>
  <ProfileTemplate title="Company">
    <CompanyHeaderCardWidget/>
    <form>
      <div class="container container-fixed-992">
        <div class="row">
          <div class="col-12">
            <FormProfileGroupSection
              v-for="(item, index) in fixedProfilePropertyForm"
              :key="index"
              :form-element="item"
              v-model:input-value="item.input.value"
              v-model:dropdown-value="item.dropdown!.value"
              v-model:label-value="item.label"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <hr/>
          </div>
        </div>
        <div class="row">
          <div class="col-12 draggable-inputs">
            <draggable
              class="list-group"
              :component-data="{
                                tag: 'div',
                                type: 'transition-group',
                                name: !drag ? 'flip-list' : null,
                              }"
              v-model="customProfilePropertyForm"
              v-bind="dragOptions"
              @start="drag = true"
              @end="drag = false"
              item-key="id"
            >
              <template #item="{ element, index }">
                <FormProfileGroupSection
                  :key="element.id"
                  :p-key="index"
                  :form-element="element"
                  v-model:input-value="element.input.value"
                  v-model:label-value="element.label"
                  v-model:dropdown-value="element.dropdown.value"
                  :draggable="true"
                  @labelActionSelected="onLabelActionSelected"
                />
              </template>
            </draggable>
          </div>
        </div>
        <div class="row action">

          <div class="d-flex justify-content-end col-9">
            <ButtonElement
              @clickOnButton="saveForm()"
              class="btn--ms btn--accent"
              style="margin-left: 6px"
            >
              Save
            </ButtonElement>
          </div>
        </div>
      </div>
    </form>
  </ProfileTemplate>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import draggable from 'vuedraggable'
import {
  ButtonElement,
  CompanyHeaderCardWidget,
  FormProfileGroupSection,
  ProfileTemplate,
} from '@shared/components'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { v4 as uuidv4 } from 'uuid'
import companyProperties, { companyDropdownProperties } from './company-properties'
import {AccountNs, CommonNs, type DropdownNs, SettingNs} from "@shared/types";
import {mapStores} from "pinia";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useCompanyStore} from "@domains/settings/stores/company.store";
import {useGeneralStore} from "@shared/stores/general.store";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'CompanyView',
  components: {
    ButtonElement,
    FormProfileGroupSection,
    CompanyHeaderCardWidget,
    ProfileTemplate,
    draggable
  },
  data () {
    return {
      companyDropdownProperties: companyDropdownProperties as DropdownNs.DropdownOptionsItem[],
      companyProperties: companyProperties as CommonNs.InputFormGroup[],

      fixedProfilePropertyForm: [] as CommonNs.InputFormGroup[],
      customProfilePropertyForm: [] as CommonNs.InputFormGroup[],

      drag: false,
      message: ['vue.draggable'],
      order: 1
    }
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations () {
    return {
      form: {
        companyName: { required }
      }
    }
  },
  computed: {
    ...mapStores(useWorkspaceStore, useCompanyStore, useGeneralStore),
    dragOptions () {
      return {
        animation: 200,
        group: 'descriptions',
        disabled: false,
        ghostClass: 'ghosty'
      }
    }
  },
  beforeMount () {
    this.initFormData()
  },
  methods: {
    initFormData () {
      this.initFixedProperties()
      this.initAccountProperties()
    },

    /** Iterate all profileProperties and filter if it is a fixed property Add these… */
    initFixedProperties () {
      const companyInfo = this.companyStore.getCompanyInfo

      this.fixedProfilePropertyForm = [] as CommonNs.InputFormGroup[]
      // Find Fixed properties
      const fixedProperties = this.companyProperties.filter((p: CommonNs.InputFormGroup) => p.isFixed)
      // Add these properties to fixedProfilePropertyForm
      fixedProperties.forEach((prop: CommonNs.InputFormGroup) => {
        const p = JSON.parse(JSON.stringify(prop))
        // Find the value of this property if present
        const propertyInAccount = companyInfo.companyProperties?.find((a: AccountNs.AccountProperty) => a.name === p.input.name)
        if (propertyInAccount) {
          p.input.value = propertyInAccount.inputValue || ''
          p.dropdown!.value = propertyInAccount.permission
        }
        this.fixedProfilePropertyForm.push(p)

        this.deleteFromDropdownMenu(p.input.name)
      })
    },

    /** Iterate all account properties and find corresponding item in… */
    initAccountProperties () {
      const companyInfo = this.companyStore.getCompanyInfo

      this.customProfilePropertyForm = [] as CommonNs.InputFormGroup[]
      companyInfo.companyProperties?.forEach((a: AccountNs.AccountProperty,
        index: number) => {
        let searchedComponentName = a.name
        if (a.isCustom) {
          searchedComponentName = AccountNs.PropertyEnums.CUSTOM
        }

        const prop = this.companyProperties.find((pp: CommonNs.InputFormGroup) => searchedComponentName === pp.input.name)
        if (prop && !prop.isFixed) {
          const p = JSON.parse(JSON.stringify(prop))
          p.input.value = a.inputValue
          p.dropdown!.value = a.permission
          p.id = index

          // Find the value of this property if present
          if (a.isCustom) {
            p.input.name = a.name
            p.input.type = a.type
            p.icon = a.icon
            p.iconType = a.iconType
            p.label = a.label
            p.isCustom = true
          } else {
            this.deleteFromDropdownMenu(p.input.name)
          }
          this.customProfilePropertyForm.push(p)
        }
      })
    },

    /** Delete this from Dropdown Menu, so the user can not add it twice */
    deleteFromDropdownMenu (subMenuName: string) {
      this.companyDropdownProperties.forEach((item: DropdownNs.DropdownOptionsItem) => {
        const index = item.subOptions?.options.findIndex((subItem: DropdownNs.DropdownOptionsItem) => subItem.value === subMenuName)
        if (index != undefined && (index > -1)) {
          item.subOptions?.options.splice(index, 1)
        }
      })
    },

    appendPropertyToForm (input: DropdownNs.DropdownOptionsItem) {
      const prop = this.companyProperties.find((pp: CommonNs.InputFormGroup) => input.value === pp.input.name)
      if (prop) {
        const p = JSON.parse(JSON.stringify(prop))
        const maxId: number = Math.max.apply(Math, this.customProfilePropertyForm.map((item) => item.id!))
        p.id = (maxId > 0 ? maxId : 0) + 1
        p.isCustom = (input.value === AccountNs.PropertyEnums.CUSTOM)

        if (p.isCustom) {
          p.input.name = uuidv4()
          p.input.type = input.inputType
          p.icon = input.icon
          p.iconType = input.iconType
        } else {
          this.deleteFromDropdownMenu(p.input.name)
        }

        this.customProfilePropertyForm.push(p)
      }
    },

    onLabelActionSelected (selectedAction: string, selectedItemIndex: number) {
      if (selectedAction === 'DUPLICATE') {
        const prop = this.customProfilePropertyForm[selectedItemIndex]
        if (prop && prop.isCustom) {
          const p = JSON.parse(JSON.stringify(prop))
          const maxId: number = Math.max.apply(Math, this.customProfilePropertyForm.map((item) => item.id!))
          p.id = (maxId > 0 ? maxId : 0) + 1
          this.customProfilePropertyForm.splice(selectedItemIndex + 1, 0, p)
        }
      } else if (selectedAction === 'DELETE') {
        this.customProfilePropertyForm.splice(selectedItemIndex, 1)
      }
    },
    saveForm () {
      const customProperties = this.customProfilePropertyForm
        .map((item: CommonNs.InputFormGroup) => ({
          name: item.input.name,
          label: item.label,
          type: item.input.type,
          isCustom: item.isCustom,
          inputValue: item.input.value,
          permission: item.dropdown?.value,
          icon: item.icon,
          iconType: item.iconType
        } as AccountNs.AccountProperty))

      const fixedProperties = this.fixedProfilePropertyForm
        .map((item: CommonNs.InputFormGroup) => ({
          name: item.input.name,
          label: item.label,
          type: item.input.type,
          isCustom: item.isCustom,
          inputValue: item.input.value,
          permission: item.dropdown?.value
        } as AccountNs.AccountProperty))

      const payload: SettingNs.UpdateCompanyInfoRequest = {
        workspaceId: this.workspaceStore.getSelectedWorkspaceId,
        companyProperties: [...fixedProperties, ...customProperties]
      }

      this.companyStore.updateCompanyProperty(payload).then(() => {
        toast.success('Saved');
        this.initFormData();
      }).catch(() => {
        toast.error('Something went wrong. Please try again.')
      })
    }

  }
})
</script>

<style lang="scss" scoped>
.action {
  margin-top: 20px;
  margin-bottom: 100px;
}
</style>
